import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Check, ChevronLeft } from "lucide-react";
import { useTranslation, type TranslationKeys } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import {
  CANCEL_REASONS,
  formatDate,
  useCancelSubscription,
  useSubscriptions,
  type CancelReason,
} from "@/lib/subscriptions";
import { MmPage } from "./shared";

type Step = "reason" | "confirm" | "done";

/**
 * MindfulMe cancel flow: Step 1 (reason) → Step 2 (are you sure?) → cancelled.
 * The subscription is cancelled at the end of the paid period (no refund).
 */
export function CancelSubscription() {
  const { id = "" } = useParams();
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const { data: subs, isLoading } = useSubscriptions();
  const cancel = useCancelSubscription();

  const [step, setStep] = useState<Step>("reason");
  const [reason, setReason] = useState<CancelReason | null>(null);
  const [message, setMessage] = useState("");
  const [reasonError, setReasonError] = useState(false);

  const sub = subs?.find((s) => s.id === id);
  const accessUntil = sub ? formatDate(sub.currentPeriodEnd, language) : "";

  if (isLoading) {
    return (
      <MmPage>
        <p className="text-sm text-slate-500">{t("profile_loading")}</p>
      </MmPage>
    );
  }
  if (!sub) {
    return (
      <MmPage backTo="/profile/subscription" backLabel={t("mm_back_to_sub")}>
        <p className="mt-6 text-base text-slate-600">{t("mm_no_subs")}</p>
      </MmPage>
    );
  }

  const keep = () => navigate("/profile/subscription");

  if (step === "done" || sub.cancelAtPeriodEnd) {
    return (
      <MmPage>
        <div className="flex flex-col items-center pt-6 text-center">
          <span className="grid size-[72px] place-items-center rounded-full bg-emerald-50 text-emerald-700">
            <Check className="size-8" aria-hidden="true" />
          </span>
          <h1 className="mt-6 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
            {t("mm_cancelled_title")}
          </h1>
          <p className="mt-3 max-w-md text-base text-slate-600">
            {t("mm_cancelled_text").replace("{date}", accessUntil)}
          </p>
          <Button asChild size="lg" className="mt-8 h-12 w-full max-w-sm rounded-full">
            <Link to="/profile">{t("mm_back_to_profile")}</Link>
          </Button>
        </div>
      </MmPage>
    );
  }

  if (step === "confirm") {
    return (
      <MmPage>
        <StepHeader label={t("mm_step_2")} onBack={() => setStep("reason")} backLabel={t("mm_back")} />
        <h1 className="mt-6 text-3xl font-extrabold tracking-tight text-slate-900">{t("mm_confirm_title")}</h1>
        <p className="mt-3 text-base text-slate-600">{t("mm_confirm_text").replace("{date}", accessUntil)}</p>
        {cancel.isError && <p className="mt-4 text-sm text-red-700">{t("mm_cancel_error")}</p>}
        <div className="mt-8 flex flex-col gap-3">
          <button
            type="button"
            disabled={cancel.isPending}
            onClick={() =>
              cancel.mutate(
                { id: sub.id, reason: reason!, message: message.trim() },
                { onSuccess: () => setStep("done") },
              )
            }
            className="h-12 w-full rounded-full border border-red-300 bg-white text-base font-medium text-red-700 transition-colors hover:bg-red-50 disabled:opacity-60"
          >
            {cancel.isPending ? t("mm_cancelling") : t("mm_cancel_sub")}
          </button>
          <Button type="button" size="lg" className="h-12 w-full rounded-full" onClick={keep} disabled={cancel.isPending}>
            {t("mm_keep_sub")}
          </Button>
        </div>
      </MmPage>
    );
  }

  return (
    <MmPage>
      <StepHeader label={t("mm_step_1")} onBack={keep} backLabel={t("mm_back_to_sub")} />
      <h1 className="mt-6 text-3xl font-extrabold tracking-tight text-slate-900">{t("mm_sorry_title")}</h1>
      <p className="mt-2 text-base text-slate-600">{t("mm_sorry_sub")}</p>

      <fieldset className="mt-6">
        <legend className="text-base font-bold text-slate-900">{t("mm_main_reason")}</legend>
        <div className="mt-3 flex flex-col gap-3">
          {CANCEL_REASONS.map((r) => (
            <label
              key={r}
              className={`flex cursor-pointer items-center gap-3 rounded-xl border bg-white px-4 py-3.5 transition-colors ${
                reason === r ? "border-primary ring-1 ring-primary" : "border-slate-200 hover:bg-slate-50"
              }`}
            >
              <input
                type="radio"
                name="cancel-reason"
                value={r}
                checked={reason === r}
                onChange={() => {
                  setReason(r);
                  setReasonError(false);
                }}
                className="size-5 accent-[var(--color-primary)]"
              />
              <span className="text-base text-slate-900">{t(`mm_reason_${r}` as TranslationKeys)}</span>
            </label>
          ))}
        </div>
        {reasonError && <p className="mt-2 text-sm text-red-700">{t("mm_select_reason")}</p>}
      </fieldset>

      <label htmlFor="cancel-message" className="mt-6 block text-sm font-semibold text-slate-900">
        {t("mm_anything_else")}
      </label>
      <textarea
        id="cancel-message"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        maxLength={1000}
        rows={3}
        className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base outline-none focus:border-primary focus:ring-1 focus:ring-primary"
      />

      <div className="mt-8 flex flex-col gap-3">
        <button
          type="button"
          onClick={() => (reason ? setStep("confirm") : setReasonError(true))}
          className="h-12 w-full rounded-full border border-red-300 bg-white text-base font-medium text-red-700 transition-colors hover:bg-red-50"
        >
          {t("mm_continue_cancel")}
        </button>
        <Button type="button" size="lg" className="h-12 w-full rounded-full" onClick={keep}>
          {t("mm_keep_sub")}
        </Button>
      </div>
    </MmPage>
  );
}

function StepHeader({ label, onBack, backLabel }: { label: string; onBack: () => void; backLabel: string }) {
  return (
    <div className="flex items-center justify-between">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1 text-sm text-slate-600 transition-colors hover:text-slate-900"
      >
        <ChevronLeft className="size-4" aria-hidden="true" />
        {backLabel}
      </button>
      <span className="text-sm text-slate-500">{label}</span>
    </div>
  );
}
